
import { t } from 'i18n';/*
 * Column presets: UI sugar, and nothing more (D3, owner decision 13.09).
 *
 * A preset fills the same `columns` rows a human could have typed, and then it
 * is gone: the saved connection carries NO trace that one was used — no
 * profile field, no marker on the stream. That is the whole point of keeping
 * it here. Deleting this file and the button that calls it removes the feature
 * completely; adding a profile is adding an entry to the list below.
 *
 * Nothing server-side knows these exist. The tap, the serializer and the
 * discovery probe are all profile-blind, so a preset can never become a
 * behaviour the platform has to keep supporting.
 *
 * They STACK (owner, 13.09): each one is a set of rows, several can be on at
 * once, and the wizard adds and removes them by set arithmetic. So the list
 * below is deliberately cut along what a person wants separately — the columns
 * every GA4 property is queried by, the purchase ones, the attribution ones —
 * rather than into one profile per imaginable use.
 */

// A GA4 export pivots its useful values out of `event_params`, which is a
// repeated STRUCT: without pivoting, the whole array lands as JSON text and
// every query downstream has to dig through it.
//
// `float64` rather than `number`: the tap declares expression types in
// BigQuery spellings, and `number` is not one of them.
const PARAM_TYPES = {
  string_value: 'string',
  int_value: 'integer',
  double_value: 'float64',
  float_value: 'float64',
};

function ga4Param(key, valueField, alias) {
  return {
    expression:
      `(SELECT value.${valueField} FROM UNNEST(event_params) WHERE key = '${key}')`,
    alias,
    type: PARAM_TYPES[valueField] || 'string',
  };
}

// A preset only ever offers rows the table in front of us actually has. The
// GA4 export schema has grown for years — `session_traffic_source_last_click`
// did not exist in 2023 — and a preset that ticked a column an older export
// lacks would fail the load rather than the wizard.
function present(rows, paths) {
  return rows.filter((row) => !row.source || paths.has(row.source));
}

// The session's last click as GA4 reports it is the cross-channel record,
// which auto-tagged ads (Google Ads, SA360, DV360) fill as well; the manual
// one has UTM tags, organic and referrals but no auto-tagged clicks, so such
// a session has nothing there. Cross-channel first, manual where it is empty
// — and only what the table has: an older export carries the manual record
// alone.
function lastClick(field, alias, paths) {
  const cross = `session_traffic_source_last_click.cross_channel_campaign.${field}`;
  const manual = `session_traffic_source_last_click.manual_campaign.${field}`;
  if (paths.has(cross) && paths.has(manual)) {
    return { expression: `COALESCE(${cross}, ${manual})`, alias, type: 'string' };
  }
  return { source: paths.has(cross) ? cross : manual, alias };
}

const GA4_CORE = {
  id: 'ga4_core',
  get label() { return t('data.ga4Standard'); },
  // What makes us offer it. Read off the columns discovery already returned —
  // no server round-trip, and no server knowledge of GA4.
  matches: (paths) =>
    ['event_params', 'event_name', 'event_date'].every((p) => paths.has(p)),
  get describe() { return t('data.theColumnsAGa4ExportIsUsuallyQueriedByEventUser'); },
  // GA4 keeps writing late events into a shard that is already closed, so a
  // run that starts at the bookmark never sees them. Re-reading the last few
  // days costs a few days of scan and is safe because the day is the replace
  // axis. Three is the usual restatement window; it is an ordinary value in
  // the saved config, indistinguishable from one a human typed.
  lookbackDays: 3,
  columns: (paths) =>
    present(
      [
        { source: 'event_date' },
        { source: 'event_timestamp' },
        { source: 'event_name' },
        { source: 'user_pseudo_id' },
        { source: 'device.category', alias: 'device_category' },
        { source: 'geo.country', alias: 'geo_country' },
        { source: 'traffic_source.source', alias: 'traffic_source' },
        { source: 'traffic_source.medium', alias: 'traffic_medium' },
        ga4Param('page_location', 'string_value', 'page_location'),
        ga4Param('page_title', 'string_value', 'page_title'),
        ga4Param('session_engaged', 'string_value', 'session_engaged'),
        ga4Param('ga_session_id', 'int_value', 'ga_session_id'),
      ],
      paths,
    ),
};

const GA4_ECOMMERCE = {
  id: 'ga4_ecommerce',
  get label() { return t('data.ga4Ecommerce'); },
  matches: (paths) => paths.has('ecommerce') || paths.has('items'),
  get describe() { return t('data.purchaseValuesFromTheEcommerceRecordPlusTheBasketItself'); },
  columns: (paths) =>
    present(
      [
        { source: 'ecommerce.transaction_id', alias: 'transaction_id' },
        { source: 'ecommerce.purchase_revenue', alias: 'purchase_revenue' },
        { source: 'ecommerce.refund_value', alias: 'refund_value' },
        { source: 'ecommerce.shipping_value', alias: 'shipping_value' },
        { source: 'ecommerce.tax_value', alias: 'tax_value' },
        { source: 'ecommerce.total_item_quantity', alias: 'total_item_quantity' },
        { source: 'ecommerce.unique_items', alias: 'unique_items' },
        // Kept whole: a purchase carries several items and flattening them
        // here would multiply every event row.
        { source: 'items' },
        ga4Param('currency', 'string_value', 'currency'),
        ga4Param('value', 'double_value', 'event_value'),
      ],
      paths,
    ),
};

const GA4_ATTRIBUTION = {
  id: 'ga4_attribution',
  get label() { return t('data.ga4Attribution'); },
  matches: (paths) =>
    paths.has('session_traffic_source_last_click') ||
    paths.has('collected_traffic_source'),
  get describe() { return t('data.whereTheSessionCameFromAsGa4ItselfAttributesItThe'); },
  columns: (paths) =>
    present(
      [
        lastClick('source', 'session_source', paths),
        lastClick('medium', 'session_medium', paths),
        lastClick('campaign_name', 'session_campaign', paths),
        {
          source:
            'session_traffic_source_last_click.cross_channel_campaign.primary_channel_group',
          alias: 'channel_group',
        },
        {
          source:
            'session_traffic_source_last_click.cross_channel_campaign.default_channel_group',
          alias: 'default_channel_group',
        },
        { source: 'collected_traffic_source.gclid', alias: 'gclid' },
        {
          source: 'collected_traffic_source.manual_campaign_id',
          alias: 'collected_campaign_id',
        },
      ],
      paths,
    ),
};

export const PRESETS = [GA4_CORE, GA4_ECOMMERCE, GA4_ATTRIBUTION];

export function presetById(id) {
  return PRESETS.find((preset) => preset.id === id) || null;
}

/** The presets worth offering for a table, given the columns discovery found. */
export function presetsFor(columns) {
  const paths = new Set((columns || []).map((c) => c.path));
  return PRESETS.filter((preset) => preset.matches(paths));
}
